"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.autoLayoutStateMachine = exports.AutoLayoutState = exports.AutoLayoutStatus = exports.CreateAutoLayoutJobResult = exports.CreateAutoLayoutJobSuccess = exports.CreateAutoLayoutJobFailure = exports.StartAutoLayoutJobRequest = exports.CreateAutoLayoutJobRequest = exports.AutoLayoutJob = exports.Started = exports.AutoLayoutIterationData = exports.AutoLayoutIterationStats = exports.dateToMillis = exports.AutoLayoutStepFeedback = exports.AutoLayoutJobMetadata = void 0;
exports.jobPathInAutoLayoutResourcesBucket = jobPathInAutoLayoutResourcesBucket;
exports.jobDescriptorPathInAutoLayoutResourcesBucket = jobDescriptorPathInAutoLayoutResourcesBucket;
exports.jobInputPathInAutoLayoutResourcesBucket = jobInputPathInAutoLayoutResourcesBucket;
exports.boardIterationPathInAutoLayoutResourcesBucket = boardIterationPathInAutoLayoutResourcesBucket;
exports.uidsFromInputBoardPath = uidsFromInputBoardPath;
exports.jobDescriptorPathFromInputBoardPath = jobDescriptorPathFromInputBoardPath;
exports.createAutoLayoutJobUid = createAutoLayoutJobUid;
exports.jobMetadataUidFromJobUid = jobMetadataUidFromJobUid;
exports.createAutoLayoutJobMetadata = createAutoLayoutJobMetadata;
exports.addIterationToRunningAutoLayoutJob = addIterationToRunningAutoLayoutJob;
exports.updateJobTimeSpent = updateJobTimeSpent;
exports.updateJobCreditsUsed = updateJobCreditsUsed;
exports.updateLastCommittedStep = updateLastCommittedStep;
exports.updateLastProcessedStep = updateLastProcessedStep;
exports.updateJobUiSummary = updateJobUiSummary;
exports.updateJobLastProcessedStep = updateJobLastProcessedStep;
exports.createAutoLayoutIterationClientData = createAutoLayoutIterationClientData;
const tslib_1 = require("tslib");
const shared_1 = require("@buildwithflux/shared");
const zod_1 = tslib_1.__importDefault(require("zod"));
const auto_layout_1 = require("../auto_layout");
const convergence_1 = require("../auto_layout/convergence");
const identifier_1 = require("../identifier");
const stateMachine_1 = require("./stateMachine");
const AutoLayoutJobMetadataError = zod_1.default.object({
    timestamp: zod_1.default.number().nonnegative(),
    message: zod_1.default.string(),
    uid: zod_1.default.string(),
});
const AutoLayoutJobUiSummary = zod_1.default.object({
    creditsUsed: zod_1.default.number().nonnegative().optional().default(0),
    creditsAllocated: zod_1.default.number().nonnegative().optional().default(0),
    computeUnits: zod_1.default.number().nonnegative().optional().default(0),
    timeSpent: zod_1.default.number().nonnegative().optional().default(0),
    allocatedTime: zod_1.default.number().nonnegative().optional().default(0),
});
const AutoLayoutJobBase = zod_1.default.object({
    projectUid: zod_1.default.string(),
    jobUid: auto_layout_1.autoLayoutJobUidSchema,
    submittedByUserUid: identifier_1.UserUid,
    userOrganizationUid: identifier_1.optionalOrganizationUid,
    type: zod_1.default.literal("autoLayout"),
    createAt: zod_1.default.number(),
    updatedAt: zod_1.default.number(),
    active: zod_1.default.boolean(),
    externalId: zod_1.default.string().uuid().optional(),
    configuration: auto_layout_1.AutoLayoutJobConfiguration,
    convergenceMatches: zod_1.default.array(convergence_1.ConvergenceMatch).optional().default([]),
    ui_summary: AutoLayoutJobUiSummary.optional(),
    lastProcessedStep: zod_1.default.number().optional().default(0),
    _internal: zod_1.default
        .object({
        project: zod_1.default.object({
            owner: zod_1.default.string(),
            slug: zod_1.default.string(),
            name: zod_1.default.string(),
            ownerUid: zod_1.default.string(),
        }),
        boardViewUrl: zod_1.default.string().optional(),
        apiClientVersion: zod_1.default.string().optional(),
        apiClientEndpoint: zod_1.default.string().optional(),
        apiClientRole: zod_1.default.string().optional(),
    })
        .optional(),
});
/*
    Job Metadata
 */
exports.AutoLayoutJobMetadata = zod_1.default.object({
    uid: auto_layout_1.autoLayoutJobMetadataUidSchema,
    startTime: zod_1.default.number().nonnegative(),
    allocatedTime: zod_1.default.number().nonnegative(),
    timeSpent: zod_1.default.number().nonnegative(),
    // Flux credits
    creditsUsed: zod_1.default.number().nonnegative(),
    creditsAllocated: zod_1.default.number().nonnegative(),
    // Deep pcb credits (obscurely named on user accessible surface)
    computeUnits: zod_1.default.number().nonnegative().optional().default(0),
    submittedByUserUid: identifier_1.UserUid,
    // For heuristic function to decide if the auto layout has converged
    lastCommittedStep: zod_1.default.number().default(0),
    // Record the TS we receive a board from DeepPcb last time, will use
    // this to calculate credit burn
    lastBoardTimestamp: zod_1.default.number().default(0),
    // These errors are user visible, and we want to show them in the UI
    // Not adding `default([])` here because existing jobs in the database
    // will not have this field
    errors: zod_1.default.array(AutoLayoutJobMetadataError).optional(),
    // Board view URL from DeepPCB for admin access to their web UI
    boardViewUrl: zod_1.default.string().optional().nullable(),
    // Track the total number of iterations processed by DeepPCB
    lastProcessedStep: zod_1.default.number().default(0),
    // Which registered API client version was last used to process this job
    externalApiVersion: zod_1.default.string().optional(),
});
exports.AutoLayoutStepFeedback = zod_1.default.discriminatedUnion("type", [
    zod_1.default.object({ type: zod_1.default.literal("like"), message: zod_1.default.literal("") }),
    zod_1.default.object({ type: zod_1.default.literal("dislike"), message: zod_1.default.string() }),
]);
const dateToMillis = (dateInput) => {
    if (typeof dateInput === "number") {
        return dateInput;
    }
    if (typeof dateInput === "string") {
        const parsedDate = new Date(dateInput);
        return isNaN(parsedDate.getTime()) ? 0 : parsedDate.getTime();
    }
    return dateInput.getTime();
};
exports.dateToMillis = dateToMillis;
/*
    Job Iteration Data
 */
exports.AutoLayoutIterationStats = zod_1.default.record(zod_1.default.string(), zod_1.default.any());
exports.AutoLayoutIterationData = zod_1.default.object({
    /**
     * Unique identifier of the iteration, generated by hashing the board
     * content
     */
    hash: auto_layout_1.autoLayoutStepHashSchema,
    /**
     * Order returned from DeepPcb, we will use this to sort iterations
     */
    order: zod_1.default.number(),
    /**
     * Url pointing to the board iteration we store in GCS
     */
    boardLocation: zod_1.default.string(),
    /**
     * Stats from generation
     */
    stats: exports.AutoLayoutIterationStats,
    /**
     * total DeepPCB credits burned at this step
     */
    computeUnits: zod_1.default.number(),
    /**
     * Whether this iteration was committed by DeepPCB
     * Default false for transitionary period to support legacy jobs without this field
     */
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    committed: zod_1.default.boolean().default(false),
    /**
     * Timestamp when this iteration was generated by DeepPCB
     * Default 0 for transitionary period to support legacy jobs without this field
     */
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    generatedOn: zod_1.default.number().nonnegative().default(0),
});
const AllocationStyle = zod_1.default.literal("cold").optional();
const Allocated = zod_1.default.object({
    ...AutoLayoutJobBase.shape,
    jobStatus: zod_1.default.literal("allocated"),
    active: zod_1.default.literal(false),
    allocationStyle: AllocationStyle,
});
const Populated = zod_1.default.object({
    ...Allocated.shape,
    jobStatus: zod_1.default.literal("populated"),
    inputBoardLocation: zod_1.default.string(),
});
const Submitted = zod_1.default.object({
    ...Populated.shape,
    jobStatus: zod_1.default.literal("submitted"),
    externalId: zod_1.default.string().uuid(),
});
const PreflightSchema = zod_1.default.object({
    warnings: zod_1.default.array(zod_1.default.string()).optional().nullable(),
    anomalies: zod_1.default.array(auto_layout_1.PreflightAnomaly).optional().nullable(),
    routingAnomalies: zod_1.default.array(auto_layout_1.PreflightAnomaly).optional().nullable(),
    placementAnomalies: zod_1.default.array(auto_layout_1.PreflightAnomaly).optional().nullable(),
});
const Ready = zod_1.default.object({
    ...Submitted.shape,
    jobStatus: zod_1.default.literal("ready"),
    preflight: PreflightSchema.optional(),
    computeLimit: zod_1.default.number().nonnegative(),
    computeScale: zod_1.default.number().nonnegative(),
});
const Booting = zod_1.default.object({
    ...Ready.shape,
    jobStatus: zod_1.default.literal("booting"),
    active: zod_1.default.literal(true),
});
exports.Started = zod_1.default.object({
    ...Booting.shape,
    jobStatus: zod_1.default.literal("started"),
    startTime: zod_1.default.number().nonnegative(),
    pausedAt: zod_1.default.undefined(),
    pausedAtCompute: zod_1.default.undefined(),
    iterations: zod_1.default.array(exports.AutoLayoutIterationData),
    feedback: zod_1.default.record(auto_layout_1.autoLayoutStepHashSchema, exports.AutoLayoutStepFeedback).optional().default({}),
    steps: zod_1.default.array(zod_1.default.custom()),
    // Used to retain the previously selected/viewed iteration
    selectedIterationHash: auto_layout_1.autoLayoutStepHashSchema.optional(),
});
const Generating = zod_1.default.object({
    ...exports.Started.shape,
    // Job could have been paused previously as well, so these fields are required
    pausedAt: zod_1.default.number().nonnegative().optional(),
    pausedAtCompute: zod_1.default.number().nonnegative().optional(),
    jobStatus: zod_1.default.literal("generating"),
});
/*
 * User manually requested pause
 */
const Pausing = zod_1.default.object({
    ...exports.Started.shape,
    // Job could have been paused previously as well, so these fields are required
    pausedAt: zod_1.default.number().nonnegative().optional(),
    pausedAtCompute: zod_1.default.number().nonnegative().optional(),
    jobStatus: zod_1.default.literal("pausing"),
});
const Paused = zod_1.default.object({
    ...exports.Started.shape,
    jobStatus: zod_1.default.literal("paused"),
    pausedAt: zod_1.default.number().nonnegative(),
    pausedAtCompute: zod_1.default.number().nonnegative(),
});
const OutOfCredit = zod_1.default.object({
    ...Paused.shape,
    jobStatus: zod_1.default.literal("outOfCredit"),
    needsPaidCreditConsent: zod_1.default.boolean().optional(),
});
const Converged = zod_1.default.object({
    ...Paused.shape,
    jobStatus: zod_1.default.literal("converged"),
});
const Timeout = zod_1.default.object({
    ...Paused.shape,
    jobStatus: zod_1.default.literal("timeout"),
});
const Resuming = zod_1.default.object({
    ...exports.Started.shape,
    jobStatus: zod_1.default.literal("resuming"),
    pausedAt: zod_1.default.number().nonnegative().optional(),
    pausedAtCompute: zod_1.default.number().nonnegative().optional(),
});
const Restarted = zod_1.default.object({
    ...exports.Started.shape,
    jobStatus: zod_1.default.literal("restarted"),
    startTime: zod_1.default.number().nonnegative(),
    pausedAt: zod_1.default.number().nonnegative().optional(),
    pausedAtCompute: zod_1.default.number().nonnegative().optional(),
});
const Discarded = zod_1.default.object({
    ...AutoLayoutJobBase.shape,
    jobStatus: zod_1.default.literal("discarded"),
    active: zod_1.default.literal(false),
    pausedAt: zod_1.default.number().nonnegative().optional(),
    pausedAtCompute: zod_1.default.number().nonnegative().optional(),
    computeLimit: zod_1.default.number().nonnegative().optional(),
});
const Applied = zod_1.default.object({
    ...exports.Started.shape,
    jobStatus: zod_1.default.literal("applied"),
    active: zod_1.default.literal(false),
    pausedAt: zod_1.default.number().nonnegative().optional(),
    pausedAtCompute: zod_1.default.number().nonnegative().optional(),
});
const Failed = zod_1.default.object({
    ...AutoLayoutJobBase.shape,
    jobStatus: zod_1.default.literal("failed"),
    reason: zod_1.default.string(),
    active: zod_1.default.literal(false),
    // Optional fields that may be present if job failed during generation
    iterations: zod_1.default.array(exports.AutoLayoutIterationData).optional(),
    feedback: zod_1.default.record(auto_layout_1.autoLayoutStepHashSchema, exports.AutoLayoutStepFeedback).optional(),
    steps: zod_1.default.array(zod_1.default.custom()).optional(),
    selectedIterationHash: auto_layout_1.autoLayoutStepHashSchema.optional(),
    preflight: PreflightSchema.optional(),
});
exports.AutoLayoutJob = zod_1.default.discriminatedUnion("jobStatus", [
    Allocated,
    Populated,
    Submitted,
    Ready,
    Booting,
    exports.Started,
    Restarted,
    Generating,
    Pausing,
    Paused,
    OutOfCredit,
    Converged,
    Timeout,
    Resuming,
    Discarded,
    Applied,
    Failed,
]);
const Status = (() => {
    const statuses = exports.AutoLayoutJob.options.map((option) => option.shape.jobStatus.value);
    return Object.fromEntries(statuses.map((status) => [status, status]));
})();
const stateSchemaMap = {
    allocated: Allocated,
    populated: Populated,
    submitted: Submitted,
    ready: Ready,
    booting: Booting,
    started: exports.Started,
    restarted: Restarted,
    generating: Generating,
    pausing: Pausing,
    paused: Paused,
    outOfCredit: OutOfCredit,
    converged: Converged,
    timeout: Timeout,
    resuming: Resuming,
    discarded: Discarded,
    applied: Applied,
    failed: Failed,
};
/**
 * Schema of request for the `createAutoLayoutJob` cloud function
 */
exports.CreateAutoLayoutJobRequest = zod_1.default.object({
    projectUid: zod_1.default.string(),
    organizationUid: identifier_1.optionalOrganizationUid,
    allocationStyle: AllocationStyle,
});
exports.StartAutoLayoutJobRequest = zod_1.default.object({
    userUid: identifier_1.UserUid,
    jobUid: auto_layout_1.autoLayoutJobUidSchema,
    organizationUid: identifier_1.optionalOrganizationUid,
    inputBoardLocation: zod_1.default.string(),
});
/**
 * Schema for the result returned by the `createAutoLayoutJob` cloud function
 */
exports.CreateAutoLayoutJobFailure = zod_1.default.object({
    type: zod_1.default.literal("failure"),
    reason: zod_1.default.string(),
});
exports.CreateAutoLayoutJobSuccess = zod_1.default.object({
    type: zod_1.default.literal("success"),
    uploadUrl: zod_1.default.string(),
    jobUid: auto_layout_1.autoLayoutJobUidSchema,
});
exports.CreateAutoLayoutJobResult = zod_1.default.discriminatedUnion("type", [
    exports.CreateAutoLayoutJobFailure,
    exports.CreateAutoLayoutJobSuccess,
]);
// Below are helper functions to build GCS path
/**
 * Path ("folder") for a job in `auto-layout-resources` bucket
 */
function jobPathInAutoLayoutResourcesBucket(userUid, jobUid) {
    return `${userUid}/${jobUid}`;
}
function jobDescriptorPathInAutoLayoutResourcesBucket(userUid, jobUid) {
    return `${jobPathInAutoLayoutResourcesBucket(userUid, jobUid)}/job.meta.json`;
}
function jobInputPathInAutoLayoutResourcesBucket(userUid, jobUid) {
    return `${jobPathInAutoLayoutResourcesBucket(userUid, jobUid)}/input/board.json`;
}
function boardIterationPathInAutoLayoutResourcesBucket(userUid, jobUid, boardFileName) {
    return `${jobPathInAutoLayoutResourcesBucket(userUid, jobUid)}/iterations/${boardFileName}.json`;
}
function uidsFromInputBoardPath(inputBoardPath) {
    const tokens = inputBoardPath.split("/");
    if (tokens.length < 2) {
        throw new Error(`invalid input board path received: ${inputBoardPath}`);
    }
    const userUid = identifier_1.UserUid.parse(tokens[0]);
    const jobUid = auto_layout_1.autoLayoutJobUidSchema.parse(tokens[1]);
    return { userUid, jobUid };
}
function jobDescriptorPathFromInputBoardPath(inputBoardPath) {
    const { userUid, jobUid } = uidsFromInputBoardPath(inputBoardPath);
    return jobDescriptorPathInAutoLayoutResourcesBucket(userUid, jobUid);
}
function createAutoLayoutJobUid() {
    return `auto-layout-${(0, shared_1.guid)()}`;
}
/**
 * Creates an AutoLayout job that's waiting for upload
 */
/**
 * Helper function to get uid for the metadata document in Firestore, and
 * we want it to relate to the job it represents
 */
function jobMetadataUidFromJobUid(jobUid) {
    return `${jobUid}-metadata`;
}
function createAutoLayoutJobMetadata(jobUid, userUid) {
    return {
        uid: jobMetadataUidFromJobUid(jobUid),
        creditsUsed: 0,
        creditsAllocated: 0,
        computeUnits: 0,
        timeSpent: 0,
        allocatedTime: 0,
        startTime: 0,
        lastCommittedStep: 0,
        lastProcessedStep: 0,
        lastBoardTimestamp: 0,
        submittedByUserUid: userUid,
        errors: [],
    };
}
// Below are state transitions
// TODO: move the following to auto-routing-core package
exports.AutoLayoutStatus = Status;
exports.AutoLayoutState = {
    states: zod_1.default.enum(exports.AutoLayoutJob.options.map((opt) => opt.shape.jobStatus.value)),
    schemas: stateSchemaMap,
    allocate: (projectUid, configuration, submittedByUserUid, organizationUid, allocationStyle) => {
        const now = new Date().getTime();
        return {
            projectUid,
            jobUid: createAutoLayoutJobUid(),
            type: "autoLayout",
            jobStatus: Status.allocated,
            allocationStyle,
            submittedByUserUid,
            userOrganizationUid: organizationUid,
            createAt: now,
            updatedAt: now,
            active: false,
            configuration: configuration,
            convergenceMatches: [],
            ui_summary: {
                creditsUsed: 0,
                creditsAllocated: 0,
                computeUnits: 0,
                timeSpent: 0,
                allocatedTime: 0,
            },
            lastProcessedStep: 0,
        };
    },
    hasFeedback: (job) => {
        return "feedback" in job;
    },
    transitionTo: {
        populated: (job, inputBoardLocation) => ({
            ...job,
            jobStatus: Status.populated,
            inputBoardLocation,
        }),
        submitted: (job, externalId) => ({
            ...job,
            jobStatus: Status.submitted,
            externalId,
        }),
        ready: (job, computeLimit, computeScale, warnings, anomalies, routingAnomalies, placementAnomalies) => ({
            ...job,
            jobStatus: Status.ready,
            preflight: { warnings, anomalies, routingAnomalies, placementAnomalies },
            computeLimit,
            computeScale,
        }),
        booting: (job) => ({
            ...job,
            jobStatus: Status.booting,
            active: true,
        }),
        started: (job, startedAt) => ({
            ...job,
            jobStatus: Status.started,
            startTime: (0, exports.dateToMillis)(startedAt),
            pausedAt: undefined,
            steps: [],
            iterations: [],
            feedback: {},
        }),
        restarted: (job, restartedAt) => ({
            ...job,
            jobStatus: Status.restarted,
            startTime: (0, exports.dateToMillis)(restartedAt),
            pausedAt: undefined,
            pausedAtCompute: undefined,
        }),
        generating: (job) => ({
            ...job,
            jobStatus: Status.generating,
        }),
        pausing: (job) => ({
            ...job,
            jobStatus: Status.pausing,
        }),
        paused: (job, pausedAt, creditsBurned) => ({
            ...job,
            jobStatus: Status.paused,
            pausedAt: (0, exports.dateToMillis)(pausedAt),
            pausedAtCompute: creditsBurned,
        }),
        outOfCredit: (job, pausedAt, creditsBurned, needsPaidCreditConsent) => ({
            ...job,
            jobStatus: Status.outOfCredit,
            pausedAt: (0, exports.dateToMillis)(pausedAt),
            pausedAtCompute: creditsBurned,
            needsPaidCreditConsent,
        }),
        converged: (job, pausedAt, creditsBurned) => ({
            ...job,
            jobStatus: Status.converged,
            pausedAt: (0, exports.dateToMillis)(pausedAt),
            pausedAtCompute: creditsBurned,
        }),
        timeout: (job, pausedAt, creditsBurned) => ({
            ...job,
            jobStatus: Status.timeout,
            pausedAt: (0, exports.dateToMillis)(pausedAt),
            pausedAtCompute: creditsBurned,
        }),
        resuming: (job, computeLimit) => ({
            ...job,
            jobStatus: Status.resuming,
            computeLimit: computeLimit ?? job.computeLimit,
        }),
        applied: (job) => ({
            ...job,
            jobStatus: Status.applied,
            active: false,
            computeLimit: job.computeLimit,
        }),
        discarded: (job) => ({
            ...job,
            jobStatus: Status.discarded,
            active: false,
            computeLimit: "computeLimit" in job ? job.computeLimit : undefined,
        }),
        failed: (job, reason, externalId) => ({
            ...job,
            jobStatus: Status.failed,
            reason,
            active: false,
            externalId: externalId ?? job.externalId,
        }),
    },
    transitionMeta: {
        populated: { from: ["allocated"], to: "populated" },
        submitted: { from: ["populated"], to: "submitted" },
        ready: { from: ["submitted"], to: "ready" },
        booting: { from: ["ready"], to: "booting" },
        started: { from: ["booting"], to: "started" },
        restarted: { from: ["resuming"], to: "restarted" },
        generating: { from: ["started", "restarted"], to: "generating" },
        pausing: { from: ["generating", "restarted", "started"], to: "pausing" },
        paused: { from: ["pausing"], to: "paused" },
        outOfCredit: { from: ["generating", "started", "pausing", "resuming", "restarted"], to: "outOfCredit" },
        converged: { from: ["generating", "pausing"], to: "converged" },
        timeout: { from: ["generating", "pausing", "started", "restarted"], to: "timeout" },
        resuming: { from: ["paused", "outOfCredit", "converged", "timeout"], to: "resuming" },
        applied: {
            from: ["generating", "pausing", "paused", "resuming", "outOfCredit", "converged", "timeout"],
            to: "applied",
        },
        // Note: While the TypeScript types allow discarded/failed from any state (AutoLayoutJobBase),
        // the runtime code in backend/auto-layout/app/src/app.ts explicitly blocks transitions
        // from applied/failed to discarded and from applied/discarded to failed.
        // This metadata reflects the actual runtime behavior for accurate visualization.
        discarded: {
            from: [
                "allocated",
                "populated",
                "submitted",
                "ready",
                "booting",
                "started",
                "restarted",
                "generating",
                "pausing",
                "paused",
                "outOfCredit",
                "converged",
                "timeout",
                "resuming",
            ],
            to: "discarded",
        },
        failed: {
            from: [
                "allocated",
                "populated",
                "submitted",
                "ready",
                "booting",
                "started",
                "restarted",
                "generating",
                "pausing",
                "paused",
                "outOfCredit",
                "converged",
                "timeout",
                "resuming",
            ],
            to: "failed",
        },
    },
};
/**
 * Pre-computed state machine graph derived from AutoLayoutState.transitionMeta.
 * Contains all states, edges (transitions), initial states, and terminal states.
 */
exports.autoLayoutStateMachine = (0, stateMachine_1.deriveStateMachine)(exports.AutoLayoutState.transitionMeta);
function addIterationToRunningAutoLayoutJob(job, iteration) {
    const updatedJob = {
        ...job,
        iterations: [...job.iterations],
    };
    updatedJob.iterations.push(iteration);
    // Sort iterations
    updatedJob.iterations.sort((iteration1, iteration2) => {
        return iteration1.order - iteration2.order;
    });
    return updatedJob;
}
function updateJobTimeSpent(oldJobMetadata) {
    const startTime = oldJobMetadata.startTime;
    const timeSpent = new Date().getTime() - startTime;
    return {
        ...oldJobMetadata,
        timeSpent,
    };
}
function updateJobCreditsUsed(oldJobMetadata, newCreditsUsed, computeUnits) {
    return {
        ...oldJobMetadata,
        creditsUsed: oldJobMetadata.creditsUsed + newCreditsUsed,
        computeUnits,
    };
}
function updateLastCommittedStep(oldJobMetadata, stepOrder, generatedAt) {
    return {
        ...oldJobMetadata,
        lastCommittedStep: stepOrder,
        lastBoardTimestamp: (0, exports.dateToMillis)(generatedAt),
    };
}
function updateLastProcessedStep(oldJobMetadata, stepOrder) {
    return {
        ...oldJobMetadata,
        lastProcessedStep: stepOrder,
    };
}
function updateJobUiSummary(job, updates) {
    const currentUiSummary = job.ui_summary || {
        creditsUsed: 0,
        creditsAllocated: 0,
        computeUnits: 0,
        timeSpent: 0,
        allocatedTime: 0,
    };
    return {
        ...job,
        ui_summary: {
            ...currentUiSummary,
            ...updates,
        },
    };
}
function updateJobLastProcessedStep(job, stepOrder) {
    return {
        ...job,
        lastProcessedStep: stepOrder,
    };
}
function createAutoLayoutIterationClientData(data) {
    return {
        ...data,
        // Setting placeholder for these fields, will update them as we load more data into this object
        nodes: {},
        loaded: false,
    };
}
//# sourceMappingURL=auto_layout.js.map